import React, { useCallback, useEffect, useState } from 'react';
import { useToastError } from 'lib/hooks';
import { useOnClickOutside } from 'lib/utils';
import { debounce } from 'lodash';
import styled from 'styled-components/macro';

type SearchResult = {
  name: string;
  source?: string;
};

interface ISearchAutocompleteProps {
  onAutocomplete?: (query: string) => Promise<SearchResult[]>;
  delay?: number;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isMenuOpen: boolean;
  searchTerm: string;
  onSearch: (term: string) => void;
}

export const ResultsMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 8px;
  max-height: 356px;
  overflow-y: auto;
  background-color: ${props => props.theme.colors.white[100]};
  border: ${props => `1px solid ${props.theme.colors.primary[10]}`};
  box-shadow:
    0px 6px 12px 0px #424f680f,
    0px 0px 4px 0px #424f6814;
  z-index: 999;
  border-radius: 8px;
  padding: 20px 0;
  box-sizing: border-box;
`;

export const MenuItem = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.theme.colors.primary[100]};
  transition: background-color 0.2s ease;
  &:hover {
    background-color: ${props => props.theme.colors.secondary[5]};
  }
`;

export const SearchAutocomplete = React.forwardRef(
  (
    {
      onAutocomplete,
      delay = 200,
      setIsMenuOpen,
      isMenuOpen,
      searchTerm,
      onSearch,
    }: ISearchAutocompleteProps,
    ref: React.ForwardedRef<boolean>
  ) => {
    const { showError } = useToastError();
    const [results, setResults] = useState<SearchResult[]>([]);

    const { ref: clickOutsideRef } = useOnClickOutside(() => {
      setIsMenuOpen(false);
    });

    useEffect(() => {
      if (isMenuOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      return () => {
        document.body.style.overflow = '';
      };
    }, [isMenuOpen]);

    const debouncedFetch = useCallback(
      debounce(async (value: string) => {
        // if there is no fetchResults provided or the value is empty, clear results and close menu
        if (!onAutocomplete || !value) {
          setResults([]);
          setIsMenuOpen(false);
          return;
        }

        try {
          const data = await onAutocomplete(value);
          const searchTermItem = {
            name: value,
          };
          // if searchTerm already exists in results, filter it out
          const filteredData = data.filter(
            item => item.name.toLowerCase() !== value.toLowerCase()
          );
          // prepend the search term item to the results and make sure it is always the first item
          setResults([searchTermItem, ...filteredData]);
          setIsMenuOpen(true);
        } catch (error) {
          showError(error || 'Failed to fetch autocomplete suggestions.');
        }
      }, delay),
      [onAutocomplete, delay]
    );

    useEffect(() => {
      if (ref && typeof ref !== 'function') {
        if (ref.current) {
          ref.current = false;
          return; // if data is submitted, do not fetch results
        }
      }
      debouncedFetch(searchTerm);
      return () => {
        debouncedFetch.cancel();
      };
    }, [searchTerm]);

    if (!isMenuOpen || results.length === 0) {
      return null;
    }

    return (
      <ResultsMenu ref={clickOutsideRef}>
        {results.map((item, idx) => (
          <MenuItem
            key={idx}
            onClick={() => {
              if (ref && typeof ref !== 'function') {
                ref.current = true;
              }
              setIsMenuOpen(false);
              onSearch(item.name);
            }}
          >
            {item.name}
          </MenuItem>
        ))}
      </ResultsMenu>
    );
  }
);
